import React, { useRef } from "react";
import { Grid } from "@mui/material";
import { useSnackbar } from "notistack";
import { ReadTextField } from "@icoach/components/";
import { Icons, Stack, IconButton, Chip, Button, Typography } from "@curves/ui-base";
import { isEqual, matchOptionsText, isEmpty, isFunction } from "@util/utils";
import useMessageDialog from "@util/hook/useMessageDialog";
import { parseDate } from "@util/moment";
import EmployeeDialogBox from "@icoach/employees/dialog/EmploeyeeDialogBox";
import { useEmployeeApi } from "@apis/useEmployeeApi";
import { EMPLOYEE_STATUS_TYPE } from "@icoach/constants/";
import EmployeeLineBindingIconButton from "@icoach/employees/compoments/EmployeeLineBindingIconButton";

const statusColor = {
	[EMPLOYEE_STATUS_TYPE.ONTHEJOB]: "success",
	[EMPLOYEE_STATUS_TYPE.RESIGN]: "error",
	[EMPLOYEE_STATUS_TYPE.FURLOUGH]: "warning",
};

const EmployeesInformationsBasicReadContent = React.forwardRef((props, ref) => {
	const { sourceData, options, onEditClick: onEditClickProps, onRefresh } = props;
	const setMessageDialog = useMessageDialog();
	const { enqueueSnackbar: _snackbar } = useSnackbar();
	const { employeeRankTypeOptions, employeeStatusOptions } = options;
	const {
		employeeName,
		nickName,
		birthday,
		jobTitleType,
		identifierID,
		isForeigner,
		email,
		mobile,
		cardNo,
		permanentPostCode,
		permanentAddress,
		correspondencePostCode,
		correspondenceAddress,
		hireDate,
		contacts,
		employeeStatus,
		resignDate: _resignDate,
		height = 0,
		weight = 0,
		employeeID,
	} = sourceData;
	const dialogRef = useRef(null);
	const { putEmployeesStatusApi } = useEmployeeApi();
	const resignDate = parseDate(_resignDate);

	const handleEditClick = () => onEditClickProps && onEditClickProps();

	const handleRefresh = () => isFunction(onRefresh) && onRefresh();

	const statusText = matchOptionsText(employeeStatusOptions, employeeStatus);

	const handleDialog = target => {
		const text = matchOptionsText(employeeStatusOptions, target);
		setMessageDialog({
			open: true,
			title: "提醒",
			onConfirm: () => handleEmployeeResignDialog(target),
			MsgComp: () => (
				<Stack alignItems="center" justifyContent="center">
					<Typography component="h4">
						確定要修改為
						<Typography component="span" variant="h5" className="px-1 font-weight-bold">
							{text}
						</Typography>
						？
					</Typography>
				</Stack>
			),
		});
	};

	const handleEmployeeResignDialog = type => {
		if (isEqual(type, EMPLOYEE_STATUS_TYPE.RESIGN)) dialogRef.current.openEmployeeResignDialog({ employeeID: employeeID, employeeStatus: EMPLOYEE_STATUS_TYPE.RESIGN }, handleRefresh);
		else {
			const params = {
				employeeStatus: type,
				employeeID: employeeID,
			};
			modifyEmployeeStatusApi(params);
		}
	};

	const modifyEmployeeStatusApi = async params => {
		const resp = await putEmployeesStatusApi(params);
		if (resp) {
			_snackbar("修改成功", { variant: "success" });
			onRefresh();
		}
	};

	return (
		<React.Fragment>
			<Stack justifyContent="space-between" alignItems="center">
				<Stack spacing="2" alignItems="center">
					<Chip label={isEqual(employeeStatus, EMPLOYEE_STATUS_TYPE.RESIGN) ? `${statusText} ${resignDate}` : statusText} size="big" color={statusColor[employeeStatus]} />
					{!isEqual(employeeStatus, EMPLOYEE_STATUS_TYPE.FURLOUGH) && isEmpty(resignDate) && (
						<Button variant="outlined" onClick={() => handleDialog(EMPLOYEE_STATUS_TYPE.FURLOUGH)} color="warning">
							留職停薪
						</Button>
					)}
					{!isEqual(employeeStatus, EMPLOYEE_STATUS_TYPE.RESIGN) && isEmpty(resignDate) && (
						<Button variant="outlined" onClick={() => handleDialog(EMPLOYEE_STATUS_TYPE.RESIGN)} color="error">
							離職
						</Button>
					)}
					{!isEmpty(resignDate) && !isEqual(employeeStatus, EMPLOYEE_STATUS_TYPE.RESIGN) && (
						<Button variant="contained" color="error" onClick={() => handleDialog(EMPLOYEE_STATUS_TYPE.ONTHEJOB)}>
							{`離職日期：${resignDate}[取消設定]`}
						</Button>
					)}
					{!isEqual(employeeStatus, EMPLOYEE_STATUS_TYPE.ONTHEJOB) && (
						<Button variant="outlined" onClick={() => handleDialog(EMPLOYEE_STATUS_TYPE.ONTHEJOB)} color="success">
							復職
						</Button>
					)}
				</Stack>
				<Stack direction="row" spacing={2}>
					<EmployeeLineBindingIconButton referenceID={employeeID}/>
					<IconButton size="small" onClick={handleEditClick}>
						<Icons.Edit />
					</IconButton>	
				</Stack>
			</Stack>
			<Grid container spacing={3}>
				<Grid xs={4} item>
					<ReadTextField label={"員工姓名"}>{employeeName}</ReadTextField>
				</Grid>
				<Grid xs={4} item>
					<ReadTextField label={"名牌名稱"}>{nickName}</ReadTextField>
				</Grid>
				<Grid xs={4} item>
					<ReadTextField label={"生日"}>{parseDate(birthday)}</ReadTextField>
				</Grid>
				<Grid xs={4} item>
					<ReadTextField label={"職稱"}>{matchOptionsText(employeeRankTypeOptions, jobTitleType)}</ReadTextField>
				</Grid>
				<Grid xs={4} item>
					<ReadTextField label={"身分證字號"}>{identifierID}</ReadTextField>
				</Grid>
				<Grid xs={4} item>
					<ReadTextField label={"非本國人"}>{isForeigner ? "是" : "否"}</ReadTextField>
				</Grid>
				<Grid xs={4} item>
					<ReadTextField label={"身高"}>{height}</ReadTextField>
				</Grid>
				<Grid xs={4} item>
					<ReadTextField label={"體重"}>{weight}</ReadTextField>
				</Grid>
				<Grid xs={4} item />
				<Grid xs={4} item>
					<ReadTextField label={"電子信箱"}>{email}</ReadTextField>
				</Grid>
				<Grid xs={4} item>
					<ReadTextField label={"手機號碼"}>{mobile}</ReadTextField>
				</Grid>
				<Grid xs={4} item>
					<ReadTextField label={"卡號"}>{cardNo}</ReadTextField>
				</Grid>
				<Grid xs={4} item>
					<ReadTextField label={"戶籍區碼"}>{permanentPostCode}</ReadTextField>
				</Grid>
				<Grid xs={8} item>
					<ReadTextField label={"戶籍地址"}>{permanentAddress}</ReadTextField>
				</Grid>
				<Grid xs={4} item>
					<ReadTextField label={"通訊區碼"}>{correspondencePostCode}</ReadTextField>
				</Grid>
				<Grid xs={8} item>
					<ReadTextField label={"通訊地址"}>{correspondenceAddress}</ReadTextField>
				</Grid>
				{contacts.map(target => {
					return (
						<React.Fragment key={target.employeeContactID}>
							<Grid xs={4} item>
								<ReadTextField label={"聯絡人姓名"}>{target.name}</ReadTextField>
							</Grid>
							<Grid xs={4} item>
								<ReadTextField label={"聯絡人電話"}>{target.mobile}</ReadTextField>
							</Grid>
							<Grid xs={4} item>
								<ReadTextField label={"與聯絡人關係"}>{target.relationship}</ReadTextField>
							</Grid>
						</React.Fragment>
					);
				})}

				<Grid xs={4} item>
					<ReadTextField label={"到職日"}>{parseDate(hireDate)}</ReadTextField>
				</Grid>
			</Grid>
			<EmployeeDialogBox ref={dialogRef} />
		</React.Fragment>
	);
});

export default EmployeesInformationsBasicReadContent;
